import { PageIcon } from "@/components/PageIcon";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import PrefTable from "@/components/PrefTable";
import { Receipt, Loader2, Plus, ArrowRight, Search } from "lucide-react";

const gbp = (v) => `£${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function InvoicesPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const load = () => api.get("/invoices").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => (items || []).filter((i) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [i.invoice_no, i.customer, i.title, i.task_legacy_id, i.quote_number].some((x) => (x || "").toString().toLowerCase().includes(s));
  }), [items, q]);

  const total = filtered.reduce((a, i) => a + (i.total || 0), 0);

  const INVOICE_COLUMNS = [
    { key: "invoice_no", label: "Invoice", mandatory: true, defaultWidth: 200, strong: true,
      render: (v, inv) => <span className="font-mono font-semibold text-indigo-700">{v}{inv.title ? <span className="text-slate-500 font-sans font-normal"> · {inv.title}</span> : ""}</span> },
    { key: "task_legacy_id", label: "Job", mono: true,
      render: (v) => <Link to={`/tasks/${v}`} onClick={(e) => e.stopPropagation()} className="text-indigo-600 hover:underline font-mono">{v}</Link> },
    { key: "customer", label: "Customer", truncate: true, render: (v) => v || "—" },
    { key: "invoice_date", label: "Date", render: (v) => v || "—" },
    { key: "status", label: "Status", render: (v, inv) => (
      <div className="flex gap-1.5">
        {[
          inv.submitted && <span key="submitted" className="text-[10px] font-semibold text-blue-700 bg-blue-50 rounded-full px-2 py-0.5">Submitted</span>,
          inv.signed_off && <span key="signed" className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">Signed off</span>,
          (!inv.submitted && !inv.signed_off) && <span key="draft" className="text-[10px] font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">Draft</span>,
        ].filter(Boolean)}
      </div>
    ) },
    { key: "total", label: "Total", align: "right", strong: true, render: (v) => gbp(v) },
  ];

  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="invoices-page">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <PageIcon />
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Invoices</h1>
            <p className="text-sm text-slate-500 mt-0.5">All demonstration invoices raised against jobs · {filtered.length} shown · {gbp(total)} total</p>
          </div>
        </div>
        <Button className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setOpen(true)} data-testid="new-invoice-btn"><Plus className="w-4 h-4" /> New Invoice</Button>
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search invoices, jobs, customers" className="pl-9" data-testid="invoices-search" />
      </div>

      {!items ? (
        <div className="p-10 flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /> Loading invoices…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-sm text-slate-400 py-16 bg-white rounded-2xl border border-dashed border-slate-200" data-testid="invoices-empty">No invoices yet. Raise one against a job.</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-3">
          <PrefTable tableId="invoices" columns={INVOICE_COLUMNS} rows={filtered} rowKey={(i) => i.id}
            onRowClick={(inv) => navigate(`/tasks/${inv.task_legacy_id}`)} rowTestId={(i) => `invoice-row-${i.id}`} testid="invoices-table-wrap" />
        </div>
      )}

      {open && <NewInvoiceDialog open={open} setOpen={setOpen} onSaved={() => { setOpen(false); load(); }} navigate={navigate} />}
    </div>
  );
}

function NewInvoiceDialog({ open, setOpen, onSaved, navigate }) {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ task_legacy_id: "", customer: "", title: "", invoice_date: today, due_date: "", reference: "", quote_number: "", total: "", submitted: false, signed_off: false });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!f.task_legacy_id.trim()) { toast.error("A job (legacy ID) is required"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/tasks/${f.task_legacy_id.trim()}/invoices`, {
        customer: f.customer || null, title: f.title || null, invoice_date: f.invoice_date, due_date: f.due_date || null,
        reference: f.reference || null, quote_number: f.quote_number || null,
        total: f.total ? Number(f.total) : null, submitted: f.submitted, signed_off: f.signed_off,
      });
      toast.success(`Created ${r.data.invoice.invoice_no}`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create invoice — check the job ID"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="new-invoice-dialog">
        <DialogHeader><DialogTitle>New invoice</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold text-slate-600">Job (legacy ID) *</label>
              <Input value={f.task_legacy_id} onChange={(e) => set("task_legacy_id", e.target.value)} placeholder="e.g. 21811" className="mt-1" data-testid="new-invoice-task" /></div>
            <div><label className="text-xs font-semibold text-slate-600">Linked quote (optional)</label>
              <Input value={f.quote_number} onChange={(e) => set("quote_number", e.target.value)} placeholder="Q-1001" className="mt-1" /></div>
            <div><label className="text-xs font-semibold text-slate-600">Customer</label>
              <Input value={f.customer} onChange={(e) => set("customer", e.target.value)} className="mt-1" /></div>
            <div><label className="text-xs font-semibold text-slate-600">Title</label>
              <Input value={f.title} onChange={(e) => set("title", e.target.value)} className="mt-1" /></div>
            <div><label className="text-xs font-semibold text-slate-600">Invoice date</label>
              <Input type="date" value={f.invoice_date} onChange={(e) => set("invoice_date", e.target.value)} className="mt-1" /></div>
            <div><label className="text-xs font-semibold text-slate-600">Due date</label>
              <Input type="date" value={f.due_date} onChange={(e) => set("due_date", e.target.value)} className="mt-1" /></div>
            <div><label className="text-xs font-semibold text-slate-600">Total (£, if no quote)</label>
              <Input type="number" step="0.01" value={f.total} onChange={(e) => set("total", e.target.value)} placeholder="0.00" className="mt-1" /></div>
            <div><label className="text-xs font-semibold text-slate-600">Reference</label>
              <Input value={f.reference} onChange={(e) => set("reference", e.target.value)} className="mt-1" /></div>
          </div>
          <p className="text-xs text-slate-400">If a linked quote is given, its total (inc VAT) is used automatically.</p>
          <div className="flex items-center justify-between"><span className="text-sm text-slate-600">Finance — Submitted</span><Switch checked={f.submitted} onCheckedChange={(v) => set("submitted", v)} /></div>
          <div className="flex items-center justify-between"><span className="text-sm text-slate-600">Finance — Signed off</span><Switch checked={f.signed_off} onCheckedChange={(v) => set("signed_off", v)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={save} disabled={busy} data-testid="new-invoice-create">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create invoice"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
